// src/pages/super-admin/EnterpriseListPage.js - 기업 계정 리스트뷰 (전체 계정)

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faUsers } from '@fortawesome/free-solid-svg-icons';
import { getEnterpriseAccounts, createEnterpriseAccount, deleteAdminUser } from '../../services/api';
import CreateEnterpriseModal from '../../components/super-admin/CreateEnterpriseModal';
import EditEnterpriseModal from '../../components/super-admin/EditEnterpriseModal';
import './EnterpriseListPage.scss';

const AUTH_TYPE_LABELS = {
  google: 'Google SSO',
  business: '기업형',
  individual: 'Individual',
};

const ROLE_LABELS = {
  owner: '소유자',
  member: '멤버',
};

function EnterpriseListPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteTargetAccount, setDeleteTargetAccount] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, per_page: 20, account_type: 'business' };
      if (search.trim()) params.search = search.trim();
      if (roleFilter) params.company_role = roleFilter;
      const data = await getEnterpriseAccounts(params);
      const rawAccounts = data.accounts || data.items || data.users || [];
      const visibleAccounts = rawAccounts.filter((account) => account.tier !== 'super' && account.account_type === 'business');
      setAccounts(visibleAccounts);
      setTotalPages(data.total_pages || 1);
      setTotalCount(Math.max((data.total_count || 0) - (rawAccounts.length - visibleAccounts.length), 0));
    } catch (err) {
      console.error('계정 목록 조회 실패:', err);
      setError('계정 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreate = async (formData) => {
    try {
      setCreateLoading(true);
      await createEnterpriseAccount(formData);
      setShowCreateModal(false);
      fetchAccounts();
    } catch (err) {
      console.error('기업 계정 생성 실패:', err);
      alert(err.message || '기업 계정 생성에 실패했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAccounts();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetAccount?.id) return;
    try {
      setDeleteLoading(true);
      await deleteAdminUser(deleteTargetAccount.id);
      setDeleteTargetAccount(null);
      if (accounts.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        fetchAccounts();
      }
    } catch (err) {
      console.error('계정 삭제 실패:', err);
      alert(err.message || '계정 삭제에 실패했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getAuthTypeLabel = (account) => {
    if (account.auth_type) return AUTH_TYPE_LABELS[account.auth_type] || account.auth_type;
    if (account.account_type === 'business') return '기업형';
    if (account.google_id || account.provider === 'google') return 'Google SSO';
    return '개인';
  };

  const getRoleLabel = (account) => {
    if (account.company_role) return ROLE_LABELS[account.company_role] || account.company_role;
    if (account.role) return ROLE_LABELS[account.role] || account.role;
    if (account.is_owner) return '소유자';
    return '멤버';
  };


  return (
    <div className="enterprise-list-page">
      {/* 페이지 헤더 */}
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <h1 className="sa-page-title">기업 계정 관리</h1>
          <p className="sa-page-subtitle">총 {totalCount}개의 기업 회원</p>
        </div>
        <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateModal(true)}>
          <FontAwesomeIcon icon={faPlus} />
          기업 계정 생성
        </button>
      </div>

      {/* 테이블 */}
      <div className="sa-table-container">
        {/* 툴바 */}
        <div className="sa-table-toolbar">
          <div className="sa-table-toolbar-left">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="sa-search-input"
                placeholder="기업명, 이메일 또는 이름 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="sa-btn sa-btn-secondary sa-btn-sm">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </form>
            <select
              className="sa-filter-select"
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            >
              <option value="">전체 역할</option>
              <option value="owner">소유자</option>
              <option value="member">멤버</option>
            </select>
          </div>
        </div>

        {/* 로딩 */}
        {loading && <div className="sa-loading">불러오는 중...</div>}

        {/* 에러 */}
        {error && (
          <div className="sa-empty">
            <div className="sa-empty-icon">⚠️</div>
            <div className="sa-empty-text">{error}</div>
            <button className="sa-btn sa-btn-secondary" onClick={fetchAccounts}>다시 시도</button>
          </div>
        )}

        {/* 테이블 */}
        {!loading && !error && (
          <>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>기업명</th>
                  <th>소속 부서</th>
                  <th>이메일</th>
                  <th>이름</th>
                  <th>역할</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="sa-empty">
                        <div className="sa-empty-icon"><FontAwesomeIcon icon={faUsers} style={{ fontSize: 36, color: '#d1d5db' }} /></div>
                        <div className="sa-empty-text">계정이 없습니다</div>
                        <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateModal(true)}>
                          <FontAwesomeIcon icon={faPlus} /> 첫 기업 계정 생성
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <span style={{ fontWeight: 600, color: '#111827' }}>
                          {account.company_name || '-'}
                        </span>
                      </td>
                      <td>{account.department || '-'}</td>
                      <td style={{ fontSize: 13, color: '#374151' }}>{account.email || '-'}</td>
                      <td style={{ fontWeight: 500, color: '#111827' }}>{account.name || '-'}</td>
                      <td>
                        <span 
                        className={`role-badge ${account.company_role === 'owner' || account.role === 'owner' || account.is_owner ? 'is-owner' : 'is-member'}`}>
                          {getRoleLabel(account)}
                        </span>
                      </td>
                      <td>
                        <div className="enterprise-actions">
                          <button
                            className="sa-btn sa-btn-secondary sa-btn-sm enterprise-text-action"
                            onClick={() => setEditingAccount(account)}
                            title="정보 수정"
                          >
                            수정
                          </button>
                          <button
                            className="sa-btn sa-btn-danger sa-btn-sm enterprise-text-action"
                            onClick={() => setDeleteTargetAccount(account)}
                            title="계정 삭제"
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="sa-pagination">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
                <span className="page-info">{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 모달 */}
      <CreateEnterpriseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
        loading={createLoading}
      />

      <EditEnterpriseModal
        isOpen={!!editingAccount}
        enterprise={editingAccount}
        onClose={() => setEditingAccount(null)}
        onUpdated={fetchAccounts}
      />

      {deleteTargetAccount && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>계정 삭제</h3>
            <p>
              <strong>{deleteTargetAccount.email || deleteTargetAccount.name || '선택한 계정'}</strong>
              을 정말로 삭제하시겠습니까?
            </p>
            <div className="delete-confirm-note">
              삭제하면 해당 계정과 계정이 생성한 산출물이 데이터에서 완전히 삭제됩니다.
            </div>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="sa-btn sa-btn-danger"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? '삭제 중...' : '예'}
              </button>
              <button
                type="button"
                className="sa-btn sa-btn-secondary"
                onClick={() => setDeleteTargetAccount(null)}
                disabled={deleteLoading}
              >
                아니오
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnterpriseListPage;
